import React, { useState } from "react";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import { saveSFQInfo, fetchSFQDetail } from "../Redux/feasibility";

const Row = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;
  font-size: 13px;
  color: #6c757d;
`;

const State = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  background: ${(p) => (p.$on ? "#e7f5ec" : "#f1f3f5")};
  color: ${(p) => (p.$on ? "#15803d" : "#6c757d")};
`;

const LinkBtn = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  font-size: 12px;
  color: #0d6efd;
  cursor: pointer;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &:hover:not(:disabled) { text-decoration: underline; }
`;

const Muted = styled.span`
  color: #adb5bd;
  font-size: 12px;
`;

// 이미 받은 동의는 지워지지 않는다 — 앞으로 묻지 않을 뿐이다.
const OFF_CONFIRM =
  "이 Study의 Site에게 개인정보 수집·이용 동의를 받지 않습니다.\n" +
  "앞으로 Site는 비밀번호만 확인하면 바로 설문을 열게 됩니다.\n\n" +
  "이미 받아둔 동의 기록은 그대로 남고, 내보내기(PDF/Word)에도 계속 표시됩니다.\n" +
  "계속하시겠습니까?";

const ON_CONFIRM =
  "이 Study의 Site는 설문을 열기 전에 개인정보 수집·이용에 동의해야 합니다.\n" +
  "아직 응답 중인 Site도 다음 접속부터 동의 화면을 먼저 보게 됩니다.\n" +
  "계속하시겠습니까?";

// Whether sites must consent before the questionnaire opens. Takes anything carrying the
// study's flag — the study detail, or an invitation detail, which serves it alongside the
// study's owner and reviewer. Missing is read as on: studies predate the switch
// (migration 037 defaults to 1), and so may a response still in flight when the frontend
// deploys. Mirrors craAssessmentEnabled in CraAssessmentToggle.js.
export const consentRequired = (row) =>
  row?.consentRequired === undefined ? true : !!row.consentRequired;

// Study 단위 개인정보 동의 사용 여부. 스터디 설정이므로 Owner(와 Admin)만 바꿀 수 있고,
// Locked/Archived에서는 나머지 스터디 정보와 함께 잠긴다 — canEdit이 그 판단을 넘겨받는다.
// 동의문 본문은 Sites 탭의 [🔒 개인정보 동의문]에서 편집한다.
export default function ConsentToggle({ sfq, canEdit }) {
  const dispatch = useDispatch();
  const [saving, setSaving] = useState(false);
  const required = consentRequired(sfq);

  async function toggle() {
    const next = !required;
    if (!window.confirm(next ? ON_CONFIRM : OFF_CONFIRM)) return;
    setSaving(true);
    try {
      await dispatch(
        saveSFQInfo({ id: sfq.id, body: { consentRequired: next } })
      ).unwrap();
      await dispatch(fetchSFQDetail(sfq.id));
    } catch (e) {
      alert(e?.message || "개인정보 동의 사용 여부 변경에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Row>
      <span>개인정보 동의:</span>
      <State $on={required}>{required ? "사용" : "사용 안 함"}</State>
      {canEdit && (
        <LinkBtn disabled={saving} onClick={toggle}>
          {required ? "사용 안 함으로 변경" : "사용으로 변경"}
        </LinkBtn>
      )}
      {required
        ? <Muted>Site는 설문을 열기 전에 동의해야 하며, 동의 여부가 감사추적과 내보내기에 기록됩니다.</Muted>
        : <Muted>Site는 동의 없이 바로 설문을 엽니다.</Muted>}
      {saving && <Muted>저장 중...</Muted>}
    </Row>
  );
}
